/**
 * Liste sorgusu sözleşmeleri (WP 2.4, ADR-0006): sürümlü filtre AST, sıralama, alan kataloğu, kayıtlı/sistem listeleri.
 * Bu dosya saf sözleşmedir: SQL üretmez. AST → SQL derleyicisi API tarafındadır (`apps/api/src/lib/list/`).
 * Public API ham SQL, `orderBy`, WHERE parçası, JSON yolu, cast veya tablo/kolon adı KABUL ETMEZ; alan adları yalnız
 * bu katalogdan (`listFieldCatalog`) gelir, operatörler tip ailesine göre allow-list'tir.
 */
import { z } from 'zod';
import { RECORD_PERMS, M2_CORE_RECORD_TYPES } from './records.js';
import { CUSTOM_FIELD_SQL_KIND, isValuelessFieldType } from './custom-fields.js';
import { fieldAccessFor, principalsMatch } from './field-access.js';
import { GENDERS, IYS_CONSENT_SOURCES } from './crm.js';
import { LEAD_CONVERTED_OPPORTUNITY_STATUSES, LEAD_STATUSES } from './leads.js';
import { ACQUISITION_CERTAINTIES } from './acquisition-certainty.js';
import { CONTRACT_STATUSES, CONTRACT_TAX_BASES } from './contracts.js';
import { ACTIVITY_COLORS, ACTIVITY_KINDS, ACTIVITY_PRIORITIES, ACTIVITY_REPEATS, } from './activities.js';
import { TICKET_CUSTOMER_COMPANY_SOURCES, TICKET_HOLD_REASONS, TICKET_PRIORITIES, TICKET_SLA_STATES, TICKET_STATUSES, TICKET_WAITING_PARTIES, } from './tickets.js';
// Yaprak modülden (konuşma modülü değil): `conversation-enums.ts` başındaki döngü notu
import { CONSENT_CHANNELS, CONSENT_CHANNEL_LABELS, CONVERSATION_PRIORITIES, CONVERSATION_STATUSES, CONSENT_STATES, } from './conversation-enums.js';
import { APPROVAL_STATES, OPPORTUNITY_OUTLOOKS, OPPORTUNITY_STATUSES, ORDER_STAGES, ORDER_STATUSES, PROPOSAL_STATUSES, } from './sales.js';
import { SALES_POTENTIAL_STATUSES } from './sales-assessment.js';
import { PRODUCT_KINDS, RECEIPT_STATES, SERVICE_STATES } from './procurement.js';
// ---------------------------------------------------------------------------
// Sınırlar (DoS korumaları) — sözleşme: docs/spec/castinflow/list-query.md §3
// ---------------------------------------------------------------------------
export const LIST_QUERY_LIMITS = {
    /** İstek gövdesi üst sınırı (bayt); rota düzeyinde denetlenir */
    maxBodyBytes: 65_536,
    maxDepth: 5,
    maxNodes: 60,
    maxChildren: 20,
    maxInValues: 100,
    /** contains_any / contains_all değer sayısı */
    maxSetValues: 20,
    maxStringLength: 500,
    maxColumns: 40,
    /** Kullanıcı sıralama alanı; sona her zaman deterministik `id` eklenir */
    maxSorts: 2,
    maxLimit: 200,
    defaultLimit: 50,
    maxCursorLength: 4096,
    /** `totalMode=auto` kesin sayım tavanı; üstünde `relation="gte"` */
    totalCap: 10_000,
    maxListsPerOwner: 100,
    listNameMax: 120,
    listDescriptionMax: 500,
};
export const FILTER_AST_VERSION = 1;
export const LIST_FIELD_KEY_RE = /^(cf\.[a-z][a-z0-9_]{1,39}|[a-z][a-zA-Z0-9]{0,39})$/;
export const FILTER_OPERATORS = [
    'eq',
    'neq',
    'contains',
    'starts_with',
    'ends_with',
    'in',
    'not_in',
    'gt',
    'gte',
    'lt',
    'lte',
    'between',
    'before',
    'after',
    'on_or_before',
    'on_or_after',
    'contains_any',
    'contains_all',
    'is_null',
    'is_not_null',
];
/** Alan davranış aileleri: operatör allow-list'i ve değer doğrulaması aileye göre yapılır. */
export const FIELD_FAMILIES = [
    'text',
    'longtext',
    'numeric',
    'date',
    'datetime',
    'boolean',
    'select',
    'multiselect',
    'ref',
    'ref_multi',
    'none',
];
export const OPERATORS_BY_FAMILY = {
    text: [
        'eq',
        'neq',
        'contains',
        'starts_with',
        'ends_with',
        'in',
        'not_in',
        'is_null',
        'is_not_null',
    ],
    // Çok satırlı/biçimli metin: yalnız alt dize araması (indekssiz; HTML çalıştırılmaz, saklanan metin üzerinde ILIKE)
    longtext: ['contains', 'is_null', 'is_not_null'],
    numeric: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'in', 'is_null', 'is_not_null'],
    date: [
        'eq',
        'before',
        'after',
        'on_or_before',
        'on_or_after',
        'between',
        'is_null',
        'is_not_null',
    ],
    datetime: [
        'eq',
        'before',
        'after',
        'on_or_before',
        'on_or_after',
        'between',
        'is_null',
        'is_not_null',
    ],
    boolean: ['eq', 'is_null', 'is_not_null'],
    select: ['eq', 'neq', 'in', 'not_in', 'is_null', 'is_not_null'],
    multiselect: ['contains_any', 'contains_all', 'is_null', 'is_not_null'],
    ref: ['eq', 'in', 'is_null', 'is_not_null'],
    ref_multi: ['contains_any', 'contains_all', 'is_null', 'is_not_null'],
    none: [],
};
/** 33 Castinflow özel alan tipi → davranış ailesi (formül: sonuç tipine göre, `customFieldFamily`). */
export const CUSTOM_FIELD_FAMILY = {
    text: 'text',
    textarea: 'longtext',
    rich_text: 'longtext',
    email: 'text',
    phone: 'text',
    url: 'text',
    integer: 'numeric',
    decimal: 'numeric',
    currency: 'numeric', // amount üzerinden
    percent: 'numeric',
    date: 'date',
    datetime: 'datetime',
    time: 'text',
    month: 'text',
    checkbox: 'boolean',
    select: 'select',
    multiselect: 'multiselect',
    rating: 'numeric',
    user: 'ref',
    department: 'ref',
    record: 'ref',
    record_multi: 'ref_multi',
    file: 'none', // anlamlı filtre/sıralama sözleşmesi yok (WP 2.5)
    image: 'none',
    address: 'none',
    location: 'none', // WP 4.2
    color: 'text',
    formula: 'text', // gerçek aile options.resultType'a göre
    iban: 'text',
    identity_no: 'text',
    country: 'text',
    digits: 'text',
    heading: 'none', // değer taşımaz; kolon kataloğuna da girmez
    files: 'none', // WP 2.5 (AK-5): filtre/sıralama sözleşmesi yok
    principal_multi: 'none', // WP 2.8 (AK-4): kolon olarak seçilebilir; filtre/sıralama sözleşmesi yok
    signature: 'none', // M6-H2: değer taşımaz; kolon kataloğuna girmez (`customListField` null döner)
};
export function customFieldFamily(def) {
    if (def.type !== 'formula')
        return CUSTOM_FIELD_FAMILY[def.type];
    switch (def.options.resultType) {
        case 'number':
            return 'numeric';
        case 'boolean':
            return 'boolean';
        case 'date':
            return 'date';
        default:
            return 'text';
    }
}
// ---------------------------------------------------------------------------
// Filtre AST (kapalı şema, sürümlü)
// ---------------------------------------------------------------------------
const scalarValue = z.union([
    z.string().max(LIST_QUERY_LIMITS.maxStringLength),
    z.number(),
    z.boolean(),
]);
const predicateValue = z.union([
    scalarValue,
    z
        .array(z.union([z.string().max(LIST_QUERY_LIMITS.maxStringLength), z.number()]))
        .max(LIST_QUERY_LIMITS.maxInValues),
]);
const predicateSchema = z
    .object({
    op: z.literal('predicate'),
    field: z.string().regex(LIST_FIELD_KEY_RE, 'geçersiz alan adı'),
    operator: z.enum(FILTER_OPERATORS),
    value: predicateValue.optional(),
})
    .strict();
export const filterNodeSchema = z.lazy(() => z.union([
    predicateSchema,
    z
        .object({
        op: z.literal('and'),
        children: z.array(filterNodeSchema).min(1).max(LIST_QUERY_LIMITS.maxChildren),
    })
        .strict(),
    z
        .object({
        op: z.literal('or'),
        children: z.array(filterNodeSchema).min(1).max(LIST_QUERY_LIMITS.maxChildren),
    })
        .strict(),
    z.object({ op: z.literal('not'), child: filterNodeSchema }).strict(),
]));
export const filterAstSchema = z
    .object({ version: z.literal(FILTER_AST_VERSION), root: filterNodeSchema })
    .strict();
/** Düğüm sayısı ve derinlik (özyinelemesiz; aşırı derin girdide yığın taşması olmaz). */
export function astStats(root) {
    let nodes = 0;
    let depth = 0;
    let predicates = 0;
    const stack = [{ n: root, d: 1 }];
    while (stack.length > 0) {
        const cur = stack.pop();
        if (!cur)
            break;
        nodes += 1;
        if (cur.d > depth)
            depth = cur.d;
        const n = cur.n;
        if (n.op === 'predicate')
            predicates += 1;
        else if (n.op === 'not')
            stack.push({ n: n.child, d: cur.d + 1 });
        else
            for (const c of n.children)
                stack.push({ n: c, d: cur.d + 1 });
        if (nodes > LIST_QUERY_LIMITS.maxNodes * 4)
            break;
    }
    return { nodes, depth, predicates };
}
/**
 * Ham girdinin derinliği (zod'dan ÖNCE, özyinelemesiz): kötü niyetli aşırı derin JSON zod'un özyinelemeli
 * ayrıştırıcısına verilmeden reddedilir.
 */
export function rawDepthExceeds(input, maxDepth, maxNodes) {
    let nodes = 0;
    const stack = [{ v: input, d: 1 }];
    while (stack.length > 0) {
        const cur = stack.pop();
        if (!cur)
            break;
        const v = cur.v;
        if (typeof v !== 'object' || v === null || Array.isArray(v))
            continue;
        nodes += 1;
        if (cur.d > maxDepth || nodes > maxNodes)
            return true;
        const o = v;
        if (Array.isArray(o['children']))
            for (const c of o['children'])
                stack.push({ v: c, d: cur.d + 1 });
        if (o['child'] !== undefined)
            stack.push({ v: o['child'], d: cur.d + 1 });
    }
    return false;
}
// ---------------------------------------------------------------------------
// Sıralama
// ---------------------------------------------------------------------------
export const sortSpecSchema = z
    .object({
    field: z.string().regex(LIST_FIELD_KEY_RE, 'geçersiz alan adı'),
    direction: z.enum(['asc', 'desc']),
    nulls: z.enum(['first', 'last']).optional(),
})
    .strict();
export const DEFAULT_SORT = [{ field: 'updatedAt', direction: 'desc' }];
// ---------------------------------------------------------------------------
// Sorgu girdisi / çıktısı
// ---------------------------------------------------------------------------
export const SYSTEM_LIST_ID_RE = /^sys:[a-z][a-z_]{1,39}$/;
export const listIdSchema = z.union([z.guid(), z.string().regex(SYSTEM_LIST_ID_RE)]);
export const recordQueryInputSchema = z
    .object({
    filter: filterAstSchema.nullable().default(null),
    /** Verilmezse: kayıtlı listenin sıralaması, o da yoksa `updatedAt desc`. Sona her zaman `id` eklenir. */
    sort: z.array(sortSpecSchema).max(LIST_QUERY_LIMITS.maxSorts).optional(),
    cursor: z.string().min(1).max(LIST_QUERY_LIMITS.maxCursorLength).nullable().default(null),
    limit: z
        .number()
        .int()
        .min(1)
        .max(LIST_QUERY_LIMITS.maxLimit)
        .default(LIST_QUERY_LIMITS.defaultLimit),
    columns: z
        .array(z.string().regex(LIST_FIELD_KEY_RE, 'geçersiz kolon adı'))
        .max(LIST_QUERY_LIMITS.maxColumns)
        .optional(),
    listId: listIdSchema.nullable().default(null),
    view: z.literal('table').default('table'),
    /** K7-2 (sahip kararı 2026-09-18): varsayılan `none` — sayım yalnız açık istemci talebiyle (`auto`), cursor'sız ilk istekte. */
    totalMode: z.enum(['none', 'auto']).default('none'),
    access: z.enum(RECORD_PERMS).default('read'),
})
    .strict();
export const recordListItemSchema = z.object({
    id: z.guid(),
    recordNo: z.string(),
    ownerId: z.guid().nullable(),
    ownerDeptId: z.guid().nullable(),
    updatedAt: z.string(),
    /** İstenen kolonların değerleri (anahtar = kolon anahtarı; `cf.<key>` dâhil) */
    values: z.record(z.string(), z.unknown()),
});
export const listTotalSchema = z.object({
    value: z.number().int(),
    exact: z.boolean(),
    /** eq: kesin · gte: tavan aşıldı, en az `value` · estimate: tahmin (bu sürümde üretilmez) */
    relation: z.enum(['eq', 'gte', 'estimate']),
});
/**
 * Tavanlı toplam sözleşmesinin TEK kaynağı (ADR-0006 · `docs/spec/castinflow/list-query.md` §2.1):
 * sayım deyimi en fazla `cap + 1` satır tarar. Taranan satır sayısı `cap`'i aşmıyorsa toplam **kesindir**
 * (`exact: true`, `relation: "eq"`); aşıyorsa dönen değer bir **alt sınırdır** (`{value: cap, exact: false,
 * relation: "gte"}`) ve planlayıcı tahmini DEĞİLDİR. `relation: "estimate"` bu sürümde üretilmez.
 */
export function cappedTotal(scannedRows, cap = LIST_QUERY_LIMITS.totalCap) {
    return scannedRows > cap
        ? { value: cap, exact: false, relation: 'gte' }
        : { value: scannedRows, exact: true, relation: 'eq' };
}
export const recordQueryResponseSchema = z.object({
    data: z.array(recordListItemSchema),
    nextCursor: z.string().nullable(),
    total: listTotalSchema.nullable(),
    queryFingerprint: z.string(),
    columns: z.array(z.string()),
});
/** Hata ayrıntı kodları: zarfın `error.details.reason` alanında taşınır (üst düzey `code` mevcut ERROR_CODES'tur). */
export const LIST_QUERY_ERROR_REASONS = [
    'invalid_filter',
    'invalid_operator',
    'invalid_field',
    'invalid_sort',
    'invalid_columns',
    'filter_too_complex',
    'invalid_cursor',
    'cursor_context_mismatch',
    'cursor_expired',
    'saved_list_not_found',
    'saved_list_version_conflict',
    'saved_list_forbidden',
    'invalid_resource',
    'limit_exceeded',
    'query_timeout',
];
const f = (key, label, family, o = {}) => {
    const filterable = o.filterable ?? true;
    return {
        key,
        label,
        source: 'builtin',
        family,
        nullable: o.nullable ?? false,
        selectable: o.selectable ?? true,
        filterable,
        sortable: o.sortable ?? false,
        operators: filterable ? OPERATORS_BY_FAMILY[family] : [],
        ...(o.refTarget === undefined ? {} : { refTarget: o.refTarget }),
        sensitivity: o.sensitivity ?? 'normal',
        ...(o.capability === undefined ? {} : { capability: o.capability }),
    };
};
const COMMON_FIELDS = [
    f('id', 'Kimlik', 'ref', { refTarget: 'record' }),
    f('recordNo', 'Kayıt no', 'numeric', { nullable: true, sortable: true }),
    f('ownerId', 'Yetki sahibi', 'ref', { nullable: true, refTarget: 'user' }),
    f('ownerDeptId', 'Sahip departman', 'ref', { nullable: true, refTarget: 'department' }),
    f('createdBy', 'Oluşturan', 'ref', { nullable: true, refTarget: 'user' }),
    f('updatedBy', 'Güncelleyen', 'ref', { nullable: true, refTarget: 'user' }),
    f('createdAt', 'Oluşturma tarihi', 'datetime', { sortable: true }),
    f('updatedAt', 'Güncelleme tarihi', 'datetime', { sortable: true }),
    f('lastActivityAt', 'Son işlem', 'datetime', { nullable: true, sortable: true }),
    f('externalRef', 'Dış referans', 'text', { nullable: true, sortable: true }),
    f('deletedAt', 'Silinme tarihi', 'datetime', { nullable: true, sensitivity: 'internal' }),
    // WP 2.5: kaydın etiketleri (`record_tags`); yalnız filtre (contains_any/contains_all/is_null/is_not_null), kolon/sıralama değil
    f('tags', 'Etiketler', 'ref_multi', { nullable: true, selectable: false, refTarget: 'tag' }),
    // WP 2.9: "Favorilerim" sistem listesi için dâhili yüklem (`record_favorites` yarı-birleşimi; yalnız eq = çağıran)
    f('favoriteOf', 'Favori (kullanıcı)', 'ref', {
        nullable: true,
        selectable: false,
        refTarget: 'user',
        sensitivity: 'internal',
    }),
];
/**
 * A3 (24 Ekim 2026): içe aktarma sonucundan "yalnız bu işte oluşturulan/güncellenen kayıtlar". Değer içe aktarma işinin kimliğidir;
 * yüklem `import_rows` yarı-birleşimiyle ve YALNIZ işi başlatan kullanıcı için derlenir (başkasının işi boş küme). Sonuç ekranındaki
 * sayı aynı süzgeçle liste ucunun toplamıdır (sayı = liste).
 */
const IMPORT_RESULT_FIELDS = [
    f('importJobCreated', 'Bu içe aktarmada oluşturulan', 'ref', {
        nullable: true,
        selectable: false,
        sensitivity: 'link',
    }),
    f('importJobUpdated', 'Bu içe aktarmada güncellenen', 'ref', {
        nullable: true,
        selectable: false,
        sensitivity: 'link',
    }),
];
/** D1 (TI-023): ürün stok durumu değerleri (liste alanı `stockState`; sunucu ifadesi `compiler.ts`) */
export const PRODUCT_STOCK_STATES = ['off', 'no_data', 'zero', 'in_stock', 'negative'];
const withChoices = (d, choiceValues) => ({
    ...d,
    choiceValues,
});
/** WP 2.9: kişi ve şirketin ortak modül alanları (migration 0031). Dizi alanları sıralanamaz. */
const CRM_SHARED_FIELDS = [
    f('relationTypes', 'İlişki tipi', 'multiselect', { nullable: true }),
    f('sourceKey', 'Kaynak', 'select', { nullable: true, sortable: true }),
    f('sectorKey', 'Sektör', 'select', { nullable: true, sortable: true }),
    f('regionKey', 'Bölge', 'select', { nullable: true, sortable: true }),
    f('isActive', 'Aktif', 'boolean', { sortable: true }),
    f('primaryEmail', 'E-posta (birincil)', 'text', { nullable: true, sortable: true }),
    f('primaryPhone', 'Telefon (birincil)', 'text', { nullable: true, sortable: true }),
    f('details', 'Detaylar', 'longtext', { nullable: true }),
    f('taxOffice', 'Vergi dairesi', 'text', { nullable: true, sortable: true }),
    f('paymentTermDays', 'Ödeme vadesi (gün)', 'numeric', { nullable: true, sortable: true }),
    // "Yaklaşan özel günler" sistem listesi için dâhili projeksiyon (AAGG); kolon/katalog değildir
    f('specialDayMd', 'Özel gün (AAGG)', 'multiselect', {
        nullable: true,
        selectable: false,
        sensitivity: 'internal',
    }),
];
/**
 * v4.6 satış potansiyeli (migration 0288; docs/spec/castinflow/satis-potansiyeli.md § 4): aday ve fırsatın güncel değerlendirmesi
 * ve takip özeti. `potential` yalnız güncel değerlendirme yüzdeyse dolu ("değerlendirilemedi" ve hiç değerlendirilmemiş kayıt
 * boş — eşik süzgecine girmez). Takip alanları (son temas, sonraki takip, açık / geciken iş) kayda bağlı aktivitelerden YALNIZ
 * ÇAĞIRANIN GÖREBİLDİKLERİYLE hesaplanır (aktivitenin kendi ACL'si; süzgeç, sıralama ve kayıtlı liste de öyle) — gizli aktivite
 * sayı, tarih ya da sıra üzerinden sızmaz; iki kullanıcı aynı kayıtta farklı değer görebilir. Gecikme: tamamlanmamış ve (başlangıç
 * günü bugünden önce ya da bitiş anı geçmiş) — "Bugün" ekranıyla aynı kural, "bugün" çalışma alanı saat diliminde.
 */
/**
 * Takip özeti (son temas, sonraki takip, açık / geciken iş): kayda bağlı ve ÇAĞIRANIN GÖREBİLDİĞİ aktivitelerden (yukarıdaki
 * kural). Tema B (21 Ekim 2026): kişi ve şirket kartlarındaki "sonraki iş" için kişi ve şirket kataloğunda da vardır — aynı
 * derleyici ifadesi ve aynı aktivite görünürlük yüklemi (yeni uç ya da yetki modeli yok).
 */
const FOLLOW_UP_FIELDS = [
    f('lastContactAt', 'Son temas', 'datetime', { nullable: true, sortable: true }),
    f('nextFollowUpOn', 'Sonraki takip', 'date', { nullable: true, sortable: true }),
    f('openTaskCount', 'Açık iş', 'numeric', { sortable: true }),
    f('overdueTaskCount', 'Geciken iş', 'numeric', { sortable: true }),
];
const SALES_POTENTIAL_FIELDS = [
    f('potential', 'Potansiyel (%)', 'numeric', { nullable: true, sortable: true }),
    withChoices(f('potentialStatus', 'Potansiyel durumu', 'select', { sortable: true }), SALES_POTENTIAL_STATUSES),
    f('potentialAssessedAt', 'Son değerlendirme', 'datetime', { nullable: true, sortable: true }),
    ...FOLLOW_UP_FIELDS,
];
/**
 * Pazarlamanın devamı (E2): pazarlama amaçlı iletişim izni — kanal başına geçerli durum (`CONSENT_STATES`: verildi / geri çekildi /
 * kayıt yok). Anahtarlar `MARKETING_CONSENT_FIELDS` (marketing-audience.ts) ile aynıdır.
 */
export const MARKETING_CONSENT_FIELD_KEYS = {
    email: 'marketingConsentEmail',
    whatsapp: 'marketingConsentWhatsapp',
    sms: 'marketingConsentSms',
    phone: 'marketingConsentPhone',
};
/** Birleşik pazarlama iletişimi durumu: izinli · izin kaydı yok · gönderim engelli (marketing-audience.ts § kural) */
export const MARKETING_CONTACT_STATE_VALUES = ['allowed', 'no_consent', 'blocked'];
const MARKETING_CONSENT_FIELD_DEFS = CONSENT_CHANNELS.map((ch) => withChoices(f(MARKETING_CONSENT_FIELD_KEYS[ch], `Pazarlama izni (${CONSENT_CHANNEL_LABELS[ch]})`, 'select', {
    sortable: true,
}), CONSENT_STATES));
const RESOURCE_FIELDS = {
    company: [
        f('name', 'Ad', 'text', { sortable: true }),
        f('tradeName', 'İşletme adı', 'text', { nullable: true, sortable: true }),
        f('taxNo', 'Vergi no', 'text', { nullable: true, sortable: true }),
        ...CRM_SHARED_FIELDS,
        ...FOLLOW_UP_FIELDS,
        ...IMPORT_RESULT_FIELDS,
    ],
    // WP 2.10 (migration 0032/0033): kategori değeri kategori kimliğidir (etiket istemcide yol); stok kolonları takip kapalıyken boş
    product: [
        f('name', 'Ad', 'text', { nullable: true, sortable: true }),
        f('code', 'Kod', 'text', { nullable: true, sortable: true }),
        f('categoryId', 'Kategori', 'select', { nullable: true }),
        f('brand', 'Marka', 'text', { nullable: true, sortable: true }),
        f('model', 'Model', 'text', { nullable: true, sortable: true }),
        f('unitKey', 'Ölçü birimi', 'select', { nullable: true, sortable: true }),
        f('salePrice', 'Satış fiyatı', 'numeric', { nullable: true, sortable: true }),
        f('saleCurrency', 'Para birimi', 'select', { nullable: true, sortable: true }),
        f('costPrice', 'Maliyet', 'numeric', {
            nullable: true,
            sortable: true,
            capability: 'products.cost_view',
        }),
        f('costCurrency', 'Maliyet para birimi', 'select', {
            nullable: true,
            sortable: true,
            capability: 'products.cost_view',
        }),
        f('vatRate', 'KDV (%)', 'numeric', { nullable: true, sortable: true }),
        f('isActive', 'Aktif', 'boolean', { sortable: true }),
        f('details', 'Detaylar', 'longtext', { nullable: true }),
        f('stockTracked', 'Stok takibi', 'boolean', { sortable: true }),
        f('stockOnHand', 'Eldeki stok', 'numeric', { nullable: true, sortable: true }),
        f('stockReserved', 'Rezerve stok', 'numeric', { nullable: true, sortable: true }),
        f('stockAvailable', 'Kullanılabilir stok', 'numeric', { nullable: true, sortable: true }),
        // D1 (TI-023): stok durumu — takip kapalı / veri yok (takipli, hiç hareket yok) / gerçek sıfır / stokta / eksi. Takip kapalı
        // ürün sıfır stoklu sayılmaz; hareket kaydı olmayan takipli ürün de "0" değil "veri yok"tur.
        withChoices(f('stockState', 'Stok durumu', 'select'), PRODUCT_STOCK_STATES),
        // G5 (migration 0300): ürün / hizmet türü; boş = türü belirtilmemiş (`is_null` süzgeciyle düzeltme listesi)
        withChoices(f('productKind', 'Tür', 'select', { nullable: true, sortable: true }), PRODUCT_KINDS),
        // D1: katalog kartının görseli (ürünün kendi taranmış dosyası); süzgeç/sıralama yok
        f('imageAttachmentId', 'Görsel', 'text', { nullable: true, filterable: false }),
        ...IMPORT_RESULT_FIELDS,
    ],
    // M3 (migration 0038): satış tipleri. Para `numeric` (kanonik metin); aşama değeri tenant aşama anahtarıdır.
    opportunity: [
        f('name', 'Konu', 'text', { sortable: true }),
        f('companyId', 'Müşteri', 'ref', { nullable: true, refTarget: 'record' }),
        f('contactId', 'İlgili kişi', 'ref', { nullable: true, refTarget: 'record' }),
        f('amount', 'Tutar', 'numeric', { sortable: true }),
        f('currency', 'Para birimi', 'select', { sortable: true }),
        f('probability', 'Olasılık (%)', 'numeric', { nullable: true, sortable: true }),
        withChoices(f('status', 'Durum', 'select', { sortable: true }), OPPORTUNITY_STATUSES),
        f('stageKey', 'Aşama', 'select', { sortable: true }),
        f('startDate', 'Başlangıç tarihi', 'date', { nullable: true, sortable: true }),
        f('expectedCloseDate', 'Tahmini sonuçlanma', 'date', { nullable: true, sortable: true }),
        f('closedOn', 'Kapanış tarihi', 'date', { nullable: true, sortable: true }),
        f('realizedAmount', 'Gerçekleşmiş tutar', 'numeric', { nullable: true, sortable: true }),
        f('lostReasonKey', 'Kayıp sebebi', 'select', { nullable: true, sortable: true }),
        withChoices(f('outlook', 'Gidişat', 'select', { nullable: true, sortable: true }), OPPORTUNITY_OUTLOOKS),
        f('details', 'Detaylar', 'longtext', { nullable: true }),
        ...SALES_POTENTIAL_FIELDS,
    ],
    proposal: [
        f('subject', 'Konu', 'text', { sortable: true }),
        f('opportunityId', 'Fırsat', 'ref', { nullable: true, refTarget: 'record' }),
        f('companyId', 'Müşteri', 'ref', { nullable: true, refTarget: 'record' }),
        f('contactId', 'İlgili kişi', 'ref', { nullable: true, refTarget: 'record' }),
        withChoices(f('status', 'Durum', 'select', { sortable: true }), PROPOSAL_STATUSES),
        withChoices(f('approvalState', 'Onay', 'select', { sortable: true }), APPROVAL_STATES),
        f('amount', 'Tutar (KDV hariç)', 'numeric', { sortable: true }),
        f('grandTotal', 'Genel toplam', 'numeric', { sortable: true }),
        f('currency', 'Para birimi', 'select', { sortable: true }),
        f('proposalDate', 'Teklif tarihi', 'date', { sortable: true }),
        f('validUntil', 'Geçerlilik', 'date', { nullable: true, sortable: true }),
        // F2 (24 Ekim 2026): faaliyet raporu "Kabul edilen teklif" sütununun süzülmüş listesi (sayı = liste)
        f('acceptedAt', 'Kabul zamanı', 'datetime', { nullable: true, sortable: true }),
    ],
    order: [
        f('subject', 'Konu', 'text', { sortable: true }),
        f('companyId', 'Müşteri', 'ref', { refTarget: 'record' }),
        f('contactId', 'İlgili kişi', 'ref', { nullable: true, refTarget: 'record' }),
        f('opportunityId', 'Fırsat', 'ref', { nullable: true, refTarget: 'record' }),
        f('sourceProposalId', 'Kaynak teklif', 'ref', { nullable: true, refTarget: 'record' }),
        withChoices(f('status', 'Durum', 'select', { sortable: true }), ORDER_STATUSES),
        withChoices(f('stage', 'Aşama', 'select', { nullable: true, sortable: true }), ORDER_STAGES),
        f('orderDate', 'Sipariş tarihi', 'date', { sortable: true }),
        f('deliveryDate', 'Teslim tarihi', 'date', { sortable: true }),
        f('amount', 'Tutar (KDV hariç)', 'numeric', { sortable: true }),
        f('grandTotal', 'Genel toplam', 'numeric', { sortable: true }),
        f('currency', 'Para birimi', 'select', { sortable: true }),
    ],
    purchase_order: [
        f('subject', 'Konu', 'text', { sortable: true }),
        f('supplierId', 'Tedarikçi', 'ref', { refTarget: 'record' }),
        f('contactId', 'İlgili kişi', 'ref', { nullable: true, refTarget: 'record' }),
        withChoices(f('status', 'Durum', 'select', { sortable: true }), ORDER_STATUSES),
        f('orderDate', 'Sipariş tarihi', 'date', { sortable: true }),
        f('deliveryDate', 'Teslim tarihi', 'date', { nullable: true, sortable: true }),
        f('amount', 'Tutar (KDV hariç)', 'numeric', { sortable: true }),
        f('grandTotal', 'Genel toplam', 'numeric', { sortable: true }),
        f('currency', 'Para birimi', 'select', { sortable: true }),
        // G5 (migration 0302/0303): mal kabulü ve hizmet aşaması durumu, onay bekleyen aşama ve açık uygunsuzluk sayısı (Tedarikçiler
        // özetinin kuyrukları aynı ifadeyle sayılır — sayı = liste)
        withChoices(f('receiptState', 'Mal kabulü', 'select', { sortable: true }), RECEIPT_STATES),
        withChoices(f('serviceState', 'Hizmet durumu', 'select', { sortable: true }), SERVICE_STATES),
        f('milestonesAwaitingApproval', 'Onay bekleyen aşama', 'numeric', { sortable: true }),
        f('openNonconformities', 'Açık uygunsuzluk', 'numeric', { sortable: true }),
    ],
    // M3 WP 3.8 (migration 0041): sözleşme. Tutar isteğe bağlı; tarihler aşamaya bağlı.
    contract: [
        f('name', 'Ad', 'text', { sortable: true }),
        f('companyId', 'Müşteri', 'ref', { refTarget: 'record' }),
        f('contactId', 'İlgili kişi', 'ref', { nullable: true, refTarget: 'record' }),
        f('opportunityId', 'Fırsat', 'ref', { nullable: true, refTarget: 'record' }),
        withChoices(f('status', 'Aşama', 'select', { sortable: true }), CONTRACT_STATUSES),
        f('amount', 'Tutar', 'numeric', { nullable: true, sortable: true }),
        f('currency', 'Para birimi', 'select', { sortable: true }),
        // G3 (sahip kararı B): tutarın vergi anlamı; boş = Bilinmiyor (eski kayıtlar varsayımla doldurulmaz)
        withChoices(f('amountTaxBasis', 'Tutarın vergi anlamı', 'select', { nullable: true, sortable: true }), CONTRACT_TAX_BASES),
        f('startDate', 'Yürürlük', 'date', { nullable: true, sortable: true }),
        f('endDate', 'Bitiş', 'date', { nullable: true, sortable: true }),
        f('signedOn', 'İmza tarihi', 'date', { nullable: true, sortable: true }),
        f('terminatedOn', 'Fesih tarihi', 'date', { nullable: true, sortable: true }),
        f('stampTax', 'Damga vergisi', 'numeric', { nullable: true, sortable: true }),
        f('details', 'Detaylar', 'longtext', { nullable: true }),
    ],
    // M4 WP 4.1 (migration 0057): aktivite. Görevli ve ilgili kayıt çok değerlidir (yan tablo); liste kolonu asıl ilgilidir.
    activity: [
        f('name', 'Yapılacak aktivite', 'text', { sortable: true }),
        withChoices(f('kind', 'Tip', 'select', { sortable: true }), ACTIVITY_KINDS),
        withChoices(f('priority', 'Öncelik', 'select', { sortable: true }), ACTIVITY_PRIORITIES),
        f('primaryRecordId', 'Asıl ilgili', 'ref', {
            nullable: true,
            refTarget: 'record',
            filterable: false,
        }),
        f('startsAt', 'Planlanan başlangıç', 'datetime', { sortable: true }),
        f('startOn', 'Başlangıç tarihi', 'date', { sortable: true }),
        f('endOn', 'Bitiş tarihi', 'date', { nullable: true, sortable: true }),
        f('progress', 'Tamamlanma (%)', 'numeric', { sortable: true }),
        f('completedAt', 'Tamamlanma zamanı', 'datetime', { nullable: true, sortable: true }),
        // F2: faaliyet raporu "Tamamlanan aktivite" (tamamlayan kullanıcı) süzülmüş listesi
        f('completedBy', 'Tamamlayan', 'ref', { nullable: true, refTarget: 'user' }),
        withChoices(f('repeat', 'Tekrar', 'select', { sortable: true }), ACTIVITY_REPEATS),
        withChoices(f('color', 'Renk', 'select', { nullable: true }), ACTIVITY_COLORS),
        f('description', 'Açıklama', 'longtext', { nullable: true }),
        // "Açık aktivitelerim" sistem listesi için dâhili yüklem (`activity_assignees` yarı-birleşimi; yalnız eq = çağıran)
        f('assignedTo', 'Görevli (kullanıcı)', 'ref', {
            nullable: true,
            selectable: false,
            refTarget: 'user',
            sensitivity: 'internal',
        }),
        // B2 (24 Ekim 2026): Genel Bakış ölçülerinin adres süzgeci — görevlisi verilen kullanıcılardan biri (ya da onların
        // departmanı) olan aktiviteler; yalnız `in`, değer kullanıcı kimlikleri. Katalogda görünmez; yine aynı ACL ile derlenir.
        f('assigneeIn', 'Görevli', 'ref', {
            nullable: true,
            selectable: false,
            refTarget: 'user',
            sensitivity: 'link',
        }),
        // G6 / K-4: teslim kaydına bağlı görevler (adres süzgeci; yalnız `in`, değer teslim kaydı kimlikleri). "Müşteri işleri"
        // görev sayısının süzülmüş listesi (sayı = liste); aktivite ACL'si aynen uygulanır.
        f('engagementIn', 'Teslim kaydı', 'ref', {
            nullable: true,
            selectable: false,
            refTarget: 'record',
            sensitivity: 'link',
        }),
    ],
    // M4 WP 4.3 (migration 0058): destek talebi. Talep sahibi serbest metindir; SLA kolonları `ticket_sla` yan tablosundan
    // türetilir (yalnız gösterim: filtre/sıralama yok). Görevli yüklemi dâhilidir ("Açık taleplerim").
    ticket: [
        f('subject', 'Konu', 'text', { sortable: true }),
        f('requesterName', 'Talep sahibi', 'text', { sortable: true }),
        f('requesterCompany', 'Talep sahibi şirketi', 'text', { nullable: true, sortable: true }),
        f('requesterEmail', 'Talep sahibi e-postası', 'text', { nullable: true }),
        f('relatedRecordId', 'Müşteri', 'ref', { nullable: true, refTarget: 'record' }),
        // Z6 T-1 (0390): müşteri kuruluşu — talep açılırken / müşterisi değişirken alınan görüntü (müşteri şirketse kendisi, kişiyse o
        // anki şirketi). Şirket ekranlarının talep sayısı ve süzülmüş listesi bu alandadır.
        f('customerCompanyId', 'Müşteri kuruluş', 'ref', { nullable: true, refTarget: 'record' }),
        // Portal kuruluş güvenliği (mevcut güvenlik G-1): kuruluş değerinin kaynağı — şirket ekranları kesin olmayan ilişkiyi ayrı belirtir
        withChoices(f('customerCompanySource', 'Müşteri kuruluş kaynağı', 'select', { nullable: true }), TICKET_CUSTOMER_COMPANY_SOURCES),
        withChoices(f('status', 'Durum', 'select', { sortable: true }), TICKET_STATUSES),
        withChoices(f('priority', 'Öncelik', 'select', { sortable: true }), TICKET_PRIORITIES),
        withChoices(f('holdReason', 'Bekleme sebebi', 'select', { nullable: true }), TICKET_HOLD_REASONS),
        f('holdUntil', 'Bekleme bitişi', 'datetime', { nullable: true, sortable: true }),
        f('typeKey', 'Tip', 'select', { nullable: true, sortable: true }),
        f('categoryKey', 'Kategori', 'select', { nullable: true, sortable: true }),
        f('sourceKey', 'Kaynak', 'select', { nullable: true, sortable: true }),
        f('closedAt', 'Kapanma tarihi', 'datetime', { nullable: true, sortable: true }),
        f('details', 'Detaylar', 'longtext'),
        f('firstResponseAt', 'İlk yanıt', 'datetime', { nullable: true, filterable: false }),
        f('firstResponseDueAt', 'Başlama hedefi', 'datetime', { nullable: true, filterable: false }),
        f('resolutionDueAt', 'Sonuçlandırma hedefi', 'datetime', { nullable: true, filterable: false }),
        // E1 (24 Ekim 2026): bekleyen taraf ve SLA durumu — destek kuyruklarının ("sayı = liste") süzgeci; kapalı talepte boş
        withChoices(f('waitingParty', 'Bekleyen taraf', 'select', { nullable: true }), TICKET_WAITING_PARTIES),
        withChoices(f('slaState', 'SLA durumu', 'select', { nullable: true }), TICKET_SLA_STATES),
        // "Açık taleplerim" sistem listesi için dâhili yüklem (`ticket_assignees` yarı-birleşimi; yalnız eq = çağıran)
        f('ticketAssignedTo', 'Görevli (kullanıcı)', 'ref', {
            nullable: true,
            selectable: false,
            refTarget: 'user',
            sensitivity: 'internal',
        }),
        // B2: aktivitedeki `assigneeIn` ile aynı kural (adres süzgeci; yalnız `in`)
        f('ticketAssigneeIn', 'Görevli', 'ref', {
            nullable: true,
            selectable: false,
            refTarget: 'user',
            sensitivity: 'link',
        }),
        // G6 / K-5: müşteri portalında gösterilen talepler (adres süzgeci; yalnız `eq true`) — güncel yayın kararı
        f('portalShown', 'Portalda gösteriliyor', 'boolean', {
            nullable: false,
            selectable: false,
            sensitivity: 'link',
        }),
    ],
    // M5 WP 5.1 (migration 0063): form belgesi. Belge değerleri formun sürüm alan setindedir (liste kolonu değildir); liste
    // başlık (ilk alan), form, form içi sıra no, onay ve arşiv durumunu gösterir. "Benim onayımı bekleyenler" dâhili yüklemdir.
    form_document: [
        f('title', 'Başlık', 'text', { nullable: true, sortable: true }),
        f('formId', 'Form', 'ref'),
        f('docNo', 'Form sıra no', 'numeric', { sortable: true }),
        f('formVersion', 'Tanım sürümü', 'numeric', { sortable: true }),
        withChoices(f('approvalState', 'Onay', 'select', { sortable: true }), APPROVAL_STATES),
        f('archivedAt', 'Arşivlenme', 'datetime', { nullable: true, sortable: true }),
        f('regionKey', 'Bölge', 'select', { nullable: true, sortable: true }),
        f('relationTypeKey', 'İlişki tipi', 'select', { nullable: true, sortable: true }),
        f('sourceKey', 'Kaynak', 'select', { nullable: true, sortable: true }),
        f('sectorKey', 'Sektör', 'select', { nullable: true, sortable: true }),
        withChoices(f('channel', 'Kanal', 'select', { sortable: true }), ['app', 'web_form']),
        f('approvalAwaitingFor', 'Onaylayabilen (kullanıcı)', 'ref', {
            nullable: true,
            selectable: false,
            refTarget: 'user',
            sensitivity: 'internal',
        }),
    ],
    // v3.1 S10 (migration 20261010_0140): ekip gelen kutusu konuşması. İleti içeriği ve müşteri tanıtıcısı liste alanı DEĞİLDİR
    // (gelen kutusu ekranı maskeli gösterir); genel liste yalnız konu, müşteri adı, durum, öncelik ve zaman damgalarını taşır.
    conversation: [
        f('subject', 'Konu', 'text', { nullable: true, sortable: true }),
        f('customerName', 'Müşteri adı', 'text', { nullable: true, sortable: true }),
        withChoices(f('status', 'Durum', 'select', { sortable: true }), CONVERSATION_STATUSES),
        withChoices(f('priority', 'Öncelik', 'select', { sortable: true }), CONVERSATION_PRIORITIES),
        f('awaitingReply', 'Yanıt bekliyor', 'boolean'),
        f('lastMessageAt', 'Son ileti', 'datetime', { sortable: true }),
        f('lastInboundAt', 'Son gelen ileti', 'datetime', { nullable: true, sortable: true }),
        f('closedAt', 'Kapanma tarihi', 'datetime', { nullable: true, sortable: true }),
    ],
    // M3 WP 3.9 (migration 0039): müşteri adayı. Ad/soyad isteğe bağlı; şirket adı ve kaynak zorunlu.
    lead: [
        f('firstName', 'Ad', 'text', { nullable: true, sortable: true }),
        f('lastName', 'Soyad', 'text', { nullable: true, sortable: true }),
        f('companyName', 'Şirket', 'text', { sortable: true }),
        f('title', 'Ünvan', 'text', { nullable: true, sortable: true }),
        withChoices(f('status', 'Durum', 'select', { sortable: true }), LEAD_STATUSES),
        // K-M3-16: kaynak tenant tanımlı `source` listesidir (sabit seçenek kümesi yok; etiketler istemcide lookup'tan)
        f('sourceKey', 'Kaynak', 'select', { sortable: true }),
        f('rating', 'Puan', 'numeric', { nullable: true, sortable: true }),
        f('email', 'E-posta', 'text', { nullable: true, sortable: true }),
        f('phone', 'Telefon', 'text', { nullable: true, sortable: true }),
        f('taxNo', 'Vergi no', 'text', { nullable: true, sortable: true }),
        f('taxOffice', 'Vergi dairesi', 'text', { nullable: true, sortable: true }),
        f('details', 'Detaylar', 'longtext', { nullable: true }),
        f('convertedAt', 'Dönüştürülme', 'datetime', { nullable: true, sortable: true }),
        // F2: faaliyet raporu "Dönüştürülen aday" (dönüştüren kullanıcı) süzülmüş listesi
        f('convertedBy', 'Dönüştüren', 'ref', { nullable: true, refTarget: 'user' }),
        // v3.1 S9 (D1′/D4′): temasın kök kampanyası (kampanya kimliği; etiket istemcide kampanya sözlüğünden) ve kaynak kesinliği.
        // Süzgeç ve dağıtım koşulu (kampanya raporu aynı tanımı kullanır). Pazarlama çalışma merkezi (24 Ekim 2026): kampanya alanları
        // kolon olarak da seçilebilir (değer kök kampanya kimliği; etiket kampanya sözlüğünden).
        f('firstTouchCampaign', 'Kampanya (ilk temas)', 'select', { nullable: true }),
        f('lastTouchCampaign', 'Kampanya (son temas)', 'select', { nullable: true }),
        // Pazarlama çalışma merkezi: temasın kaynağı — kampanya raporunun satır kaynağıyla BİREBİR aynı ifade
        // (`COALESCE(temas.source_key, aday.source_key)`; teması olmayan adayda adayın kaynağı). Etiket kaynak sözlüğünden.
        f('firstTouchSource', 'Kaynak (ilk temas)', 'select', { sortable: true }),
        f('lastTouchSource', 'Kaynak (son temas)', 'select', { sortable: true }),
        withChoices(f('firstTouchCertainty', 'Kaynak kesinliği (ilk temas)', 'select', {
            nullable: true,
            selectable: false,
        }), ACQUISITION_CERTAINTIES),
        withChoices(f('lastTouchCertainty', 'Kaynak kesinliği (son temas)', 'select', {
            nullable: true,
            selectable: false,
        }), ACQUISITION_CERTAINTIES),
        // Pazarlama çalışma merkezi (24 Ekim 2026; docs/spec/castinflow/pazarlama-calisma-merkezi.md). İki alan da bağlı kaydın
        // KENDİ okuma yetkisiyle hesaplanır (liste motorunun kayıt okuma yüklemi; takip alanlarındaki aktivite deseni):
        // - yanıt bekleyen konuşma: adaya bağlı (`conversations.lead_id`), kapanmamış ve müşteri yanıtı bekleyen (`awaiting_reply`)
        //   ve ÇAĞIRANIN GÖREBİLDİĞİ en az bir konuşma var mı — görülemeyen konuşma değer, süzgeç ya da sayı üzerinden sızmaz;
        // - dönüşen fırsatın durumu: `LEAD_CONVERTED_OPPORTUNITY_STATUSES`; fırsat görülemiyor/silinmiş/yoksa boş.
        f('awaitingReplyConversation', 'Yanıt bekleyen konuşma', 'boolean'),
        withChoices(f('convertedOpportunityStatus', 'Dönüşen fırsatın durumu', 'select', { nullable: true }), LEAD_CONVERTED_OPPORTUNITY_STATUSES),
        // Pazarlamanın devamı (E2, 24 Ekim 2026; docs/spec/castinflow/pazarlama-devam.md § 2): iletişim izni, gönderim engeli ve
        // temas sıklığı. İzin = izin defterinin (`consent_records`, yalnız ekleme) pazarlama amaçlı EN SON satırı; kayıt satırları + adayın
        // e-posta/telefon tanıtıcısına yazılmış satırlar (konuşma kayda bağlanmadan gelen "DUR"). Bastırma = e-posta adresi etkin
        // bastırma listesinde. Temas sayıları = ortak iletişim kurallarının temas defteri (üç aktör toplamı; kayan 24 saat / 7 gün;
        // `decideContactWithin` ile aynı sayım). İçe aktarma ya da araştırma izin ÜRETMEZ; bu alanlar yalnız okunur.
        ...MARKETING_CONSENT_FIELD_DEFS,
        f('emailSuppressed', 'E-posta gönderim engelinde', 'boolean'),
        // G3 (sahip kararı C): genel iletişimden çıkış ya da kapsamı belirtilmemiş (eski) geri çekme hâlâ geçerli — bütün kanallar kapalı
        f('marketingGeneralOptOut', 'Genel iletişimden çıkış', 'boolean'),
        withChoices(f('marketingContactState', 'Pazarlama iletişimi', 'select', { sortable: true }), MARKETING_CONTACT_STATE_VALUES),
        f('proactiveContacts24h', 'Kendiliğinden temas (son 24 saat)', 'numeric', { sortable: true }),
        f('proactiveContacts7d', 'Kendiliğinden temas (son 7 gün)', 'numeric', { sortable: true }),
        // İlk temas (kalite analizi "ilk temas süresi"): görülebilen, tamamlanmış, iş/görev dışı bağlı aktivitenin EN ESKİSİ
        f('firstContactAt', 'İlk temas', 'datetime', { nullable: true, sortable: true }),
        ...SALES_POTENTIAL_FIELDS,
        ...IMPORT_RESULT_FIELDS,
    ],
    contact: [
        f('firstName', 'Ad', 'text', { sortable: true }),
        f('lastName', 'Soyad', 'text', { sortable: true }),
        f('title', 'Ünvan', 'text', { nullable: true, sortable: true }),
        f('companyId', 'Şirket', 'ref', { nullable: true, refTarget: 'record' }),
        withChoices(f('gender', 'Cinsiyet', 'select', { nullable: true }), GENDERS),
        // Hassas: varsayılan kolonlarda yok; sıralanamaz (K-3, §25)
        f('identityNo', 'Kimlik no', 'text', { nullable: true }),
        f('iysConsentAt', 'İYS izin tarihi', 'date', { nullable: true, sortable: true }),
        withChoices(f('iysConsentSource', 'İYS izin kaynağı', 'select', { nullable: true }), IYS_CONSENT_SOURCES),
        ...CRM_SHARED_FIELDS,
        ...FOLLOW_UP_FIELDS,
        ...IMPORT_RESULT_FIELDS,
    ],
};
export const DEFAULT_COLUMNS = {
    form_document: ['docNo', 'title', 'approvalState', 'ownerId', 'createdAt', 'updatedAt'],
    conversation: [
        'recordNo',
        'subject',
        'customerName',
        'status',
        'priority',
        'lastMessageAt',
        'ownerId',
    ],
    activity: ['name', 'kind', 'primaryRecordId', 'startsAt', 'endOn', 'progress', 'ownerId'],
    // Keşif O20: #, oluşturma, konu, müşteri, talep sahibi, öncelik, durum, sonuçlandırma hedefi, görevli (sahip)
    ticket: [
        'recordNo',
        'createdAt',
        'subject',
        'relatedRecordId',
        'requesterName',
        'priority',
        'status',
        'resolutionDueAt',
        'ownerId',
    ],
    lead: [
        'firstName',
        'lastName',
        'companyName',
        'status',
        'sourceKey',
        'email',
        'phone',
        'ownerId',
    ],
    contract: [
        'recordNo',
        'name',
        'companyId',
        'status',
        'amount',
        'currency',
        'startDate',
        'endDate',
    ],
    company: ['name', 'relationTypes', 'primaryPhone', 'primaryEmail', 'ownerId', 'updatedAt'],
    product: ['name', 'code', 'categoryId', 'salePrice', 'saleCurrency', 'isActive', 'updatedAt'],
    opportunity: [
        'name',
        'companyId',
        'stageKey',
        'status',
        'amount',
        'currency',
        'expectedCloseDate',
        'ownerId',
    ],
    proposal: [
        'recordNo',
        'subject',
        'companyId',
        'status',
        'approvalState',
        'grandTotal',
        'currency',
        'proposalDate',
    ],
    order: [
        'recordNo',
        'subject',
        'companyId',
        'status',
        'stage',
        'deliveryDate',
        'grandTotal',
        'currency',
    ],
    purchase_order: [
        'recordNo',
        'subject',
        'supplierId',
        'status',
        'deliveryDate',
        'grandTotal',
        'currency',
    ],
    contact: [
        'firstName',
        'lastName',
        'title',
        'companyId',
        'primaryPhone',
        'primaryEmail',
        'ownerId',
    ],
};
export function isListResource(t) {
    return M2_CORE_RECORD_TYPES.includes(t);
}
/**
 * Özel alan tek başına okunabilir mi (yalnız `readPrincipals`)? Bağımlılık kuralı (formül/koşul) dâhil nihai karar
 * `fieldAccessFor` ile verilir; katalog ve tüm yanıt yüzeyleri onu kullanır (WP 2.8 K-1/K-2).
 */
export function canReadCustomField(def, viewer) {
    return principalsMatch(def.readPrincipals, viewer);
}
export function customListField(def) {
    if (isValuelessFieldType(def.type))
        return null; // değer taşımaz (başlık, imza alanı); kolon kataloğuna girmez
    const family = customFieldFamily(def);
    const filterable = family !== 'none';
    const indexable = def.type === 'formula' || CUSTOM_FIELD_SQL_KIND[def.type] !== null;
    const sortable = filterable &&
        indexable &&
        (family === 'text' ||
            family === 'numeric' ||
            family === 'date' ||
            family === 'datetime' ||
            family === 'boolean' ||
            family === 'select');
    const refTarget = def.type === 'user'
        ? 'user'
        : def.type === 'department'
            ? 'department'
            : def.type === 'record' || def.type === 'record_multi'
                ? 'record'
                : undefined;
    return {
        key: `cf.${def.key}`,
        label: def.label,
        source: 'custom',
        family,
        customType: def.type,
        nullable: true,
        selectable: true,
        filterable,
        sortable,
        operators: filterable ? OPERATORS_BY_FAMILY[family] : [],
        ...(refTarget === undefined ? {} : { refTarget }),
        ...(def.type === 'select' || def.type === 'multiselect'
            ? { choiceValues: (def.options.choices ?? []).map((c) => c.value) }
            : {}),
        sensitivity: 'normal',
    };
}
/**
 * Bir kaynağın alan kataloğu. `includeInternal` yalnız sunucu içi (sistem listeleri) içindir.
 * Silinmiş özel alan tanımları `defs` içinde yer almaz (repo yalnız aktif tanımları döner) → yeni sorguda kullanılamaz.
 */
export function listFieldCatalog(resource, defs, viewer, opts = {}) {
    const out = new Map();
    for (const d of [...RESOURCE_FIELDS[resource], ...COMMON_FIELDS]) {
        if (d.sensitivity === 'internal' && opts.includeInternal !== true)
            continue;
        if (d.sensitivity === 'link' && opts.includeLink !== true && opts.includeInternal !== true)
            continue;
        // Yetenek kısıtlı yerleşik alan (maliyet): yetkisiz çağıran için alan YOK (filtre/sıralama/kolon aynı "bilinmeyen alan")
        if (d.capability !== undefined &&
            !viewer.unrestricted &&
            !(viewer.capabilities ?? []).includes(d.capability))
            continue;
        out.set(d.key, d);
    }
    const own = defs.filter((d) => d.recordType === resource);
    const { readable } = fieldAccessFor(own, viewer);
    for (const def of own) {
        if (!readable.has(def.key))
            continue;
        const lf = customListField(def);
        if (lf)
            out.set(lf.key, lf);
    }
    return out;
}
export const listFieldDefSchema = z.object({
    key: z.string(),
    label: z.string(),
    source: z.enum(['builtin', 'custom']),
    family: z.enum(FIELD_FAMILIES),
    customType: z.string().optional(),
    nullable: z.boolean(),
    selectable: z.boolean(),
    filterable: z.boolean(),
    sortable: z.boolean(),
    operators: z.array(z.enum(FILTER_OPERATORS)),
    refTarget: z.enum(['user', 'department', 'record', 'tag']).optional(),
    choiceValues: z.array(z.string()).optional(),
    sensitivity: z.enum(['normal', 'internal', 'link']),
});
export const listCatalogResponseSchema = z.object({
    resource: z.enum(M2_CORE_RECORD_TYPES),
    fields: z.array(listFieldDefSchema),
    defaultColumns: z.array(z.string()),
    defaultSort: z.array(sortSpecSchema),
    /** M6 WP 6.2: çalışma alanı liste varsayılanının sayfa boyutu (ayar yoksa alan yoktur; arayüz kendi varsayılanını kullanır) */
    defaultPageSize: z.number().int().optional(),
    limits: z.record(z.string(), z.number()),
});
// ---------------------------------------------------------------------------
// Kanonikleştirme (parmak izi girdisi) — değer normalizasyonu derleyicide yapılır; burada yapı normalize edilir
// ---------------------------------------------------------------------------
function canonValue(v, operator) {
    if (v === undefined)
        return null;
    if (Array.isArray(v)) {
        const arr = v.map((x) => (typeof x === 'number' ? String(x) : x));
        // between sıralıdır; küme operatörlerinde sıra ve tekrar anlamsızdır
        return operator === 'between' ? arr : [...new Set(arr)].sort();
    }
    return typeof v === 'number' ? String(v) : v;
}
/** Yapısal kanonik biçim: AND/OR çocukları sıralanır ve tekilleştirilir; tek çocuklu AND/OR açılır; çift NOT sadeleşir. */
export function canonicalizeFilter(node) {
    if (node.op === 'predicate') {
        return ['p', node.field, node.operator, canonValue(node.value, node.operator)];
    }
    if (node.op === 'not') {
        const inner = node.child;
        if (inner.op === 'not')
            return canonicalizeFilter(inner.child);
        return ['not', canonicalizeFilter(inner)];
    }
    const flat = [];
    const stack = [...node.children];
    while (stack.length > 0) {
        const c = stack.shift();
        if (!c)
            break;
        // Aynı operatörlü iç içe gruplar birleşir: and(and(a,b),c) ≡ and(a,b,c)
        if (c.op === node.op)
            stack.unshift(...c.children);
        else
            flat.push(c);
    }
    const parts = [...new Set(flat.map((c) => JSON.stringify(canonicalizeFilter(c))))].sort();
    if (parts.length === 1)
        return JSON.parse(parts[0] ?? 'null');
    return [node.op, parts.map((p) => JSON.parse(p))];
}
// ---------------------------------------------------------------------------
// Kayıtlı listeler ve sistem listeleri
// ---------------------------------------------------------------------------
export const savedListSchema = z.object({
    id: z.guid(),
    resource: z.enum(M2_CORE_RECORD_TYPES),
    name: z.string(),
    description: z.string().nullable(),
    filter: filterAstSchema.nullable(),
    sort: z.array(sortSpecSchema),
    columns: z.array(z.string()),
    isPublic: z.boolean(),
    ownerUserId: z.guid().nullable(),
    /** Çağıran bu listeyi değiştirebilir/silebilir mi */
    canManage: z.boolean(),
    /**
     * WP 2.8: liste çağıranın okuyamadığı (veya tanımı silinmiş) bir alana başvuruyorsa `false`; bu durumda tanım
     * (filtre/sıralama/kolon) boş döner ve çalıştırma alan adı vermeyen `saved_list_forbidden` alır.
     */
    available: z.boolean().default(true),
    version: z.number().int(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** Kayıtlı liste tanımının başvurduğu alan anahtarları (filtre yüklemleri + sıralama + kolonlar). */
export function savedListFieldRefs(def) {
    const out = new Set();
    if (def.filter !== null) {
        const stack = [def.filter.root];
        while (stack.length > 0) {
            const n = stack.pop();
            if (!n)
                break;
            if (n.op === 'predicate')
                out.add(n.field);
            else if (n.op === 'not')
                stack.push(n.child);
            else
                stack.push(...n.children);
        }
    }
    for (const s of def.sort)
        out.add(s.field);
    for (const c of def.columns)
        out.add(c);
    return out;
}
const listCore = {
    name: z.string().trim().min(1).max(LIST_QUERY_LIMITS.listNameMax),
    description: z.string().trim().max(LIST_QUERY_LIMITS.listDescriptionMax).nullable().default(null),
    filter: filterAstSchema.nullable().default(null),
    sort: z.array(sortSpecSchema).max(LIST_QUERY_LIMITS.maxSorts).default([]),
    columns: z
        .array(z.string().regex(LIST_FIELD_KEY_RE, 'geçersiz kolon adı'))
        .max(LIST_QUERY_LIMITS.maxColumns)
        .default([]),
    isPublic: z.boolean().default(false),
};
export const createSavedListInputSchema = z.object(listCore).strict();
export const updateSavedListInputSchema = z
    .object({
    /** İyimser eşzamanlılık: beklenen sürüm (If-Match başlığıyla da verilebilir; ikisi de varsa eşit olmalı) */
    expectedVersion: z.number().int().min(1).optional(),
    name: listCore.name.optional(),
    description: z.string().trim().max(LIST_QUERY_LIMITS.listDescriptionMax).nullable().optional(),
    filter: filterAstSchema.nullable().optional(),
    sort: z.array(sortSpecSchema).max(LIST_QUERY_LIMITS.maxSorts).optional(),
    columns: z
        .array(z.string().regex(LIST_FIELD_KEY_RE, 'geçersiz kolon adı'))
        .max(LIST_QUERY_LIMITS.maxColumns)
        .optional(),
    isPublic: z.boolean().optional(),
})
    .strict();
export const systemListSchema = z.object({
    id: z.string().regex(SYSTEM_LIST_ID_RE),
    titleKey: z.string(),
    title: z.string(),
    resource: z.enum(M2_CORE_RECORD_TYPES),
    sort: z.array(sortSpecSchema),
    columns: z.array(z.string()),
});
export const listsResponseSchema = z.object({
    system: z.array(systemListSchema),
    saved: z.array(savedListSchema),
});
//# sourceMappingURL=list-query.js.map